/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import { useRef, useState } from 'react';
import smoothScrollIntoView from 'smooth-scroll-into-view-if-needed';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useBestFitMutation } from '../Hooks/Api/useBestFitMutation';

/**
 * Custom useTrialSessionForm hook
 * @param setNextButtonIsActive
 * @param sitePackage
 */
export const useTrialSessionForm = (setNextButtonIsActive, sitePackage) => {
  const { t } = useTranslation();
  const [lastValidStep, setLastValidStep] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [errorCode, setErrorCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const stepperLabels = [t('studio'), t('appointment'), t('personalData'), t('summary')];
  const isLastStep = activeStep === stepperLabels.length - 1;
  const { mutate } = useBestFitMutation('bookTrialSession');
  const formTemplate = document.querySelector('.form-template');
  const firstStepHeader = formTemplate.content.querySelector('[data-identifier="firstStepHeader"]');
  const firstStepContent = formTemplate.content.querySelector('[data-identifier="firstStepContent"]');
  const secondStepHeader = formTemplate.content.querySelector('[data-identifier="secondStepHeader"]');
  const secondStepContent = formTemplate.content.querySelector('[data-identifier="secondStepContent"]');
  const thirdStepHeader = formTemplate.content.querySelector('[data-identifier="thirdStepHeader"]');
  const thirdStepContent = formTemplate.content.querySelector('[data-identifier="thirdStepContent"]');
  const fourthStepHeader = formTemplate.content.querySelector('[data-identifier="fourthStepHeader"]');
  const fourthStepContent = formTemplate.content.querySelector('[data-identifier="fourthStepContent"]');
  const confirmationHeader = formTemplate.content.querySelector('[data-identifier="confirmationHeader"]');
  const confirmationContent = formTemplate.content.querySelector('[data-identifier="confirmationContent"]');
  const submittedFormValues = useRef(null);

  /**
     * Handle success
     * @param data
     */
  const onSuccess = (data) => {
    setIsLoading(false);
    setLastValidStep(activeStep + 1);
    setActiveStep(activeStep + 1);
    // Push trialSubmit event to dataLayer
    // eslint-disable-next-line no-undef
    if (dataLayer) {
      // eslint-disable-next-line no-undef
      dataLayer.push({ event: 'trialSubmit', trialSubmitId: data.data.id });
    }
  };

  /**
     * Logs an error to the Neos Middleware.
     *
     * This function sends a POST request to the specified endpoint to log
     * error details related to the trial session process. It includes information
     * such as site package, submitted form values, and error specifics like
     * error codes, messages, and trace IDs.
     *
     * @param {Object} error - The error object containing details about the error.
     * @param {Object} error.response - The response object from the error.
     * @param {Object} error.response.data - The data containing error specifics.
     * @param {Array} error.response.data.errorCodes - An array of error codes.
     * @param {string} error.response.data.message - A descriptive error message.
     * @param {string} error.response.data.traceId - A unique identifier for tracing the error.
     */
  const logMagiclineError = (error) => {
    const payload = {
      sitePackage,
      formValues: submittedFormValues.current,
      error: {
        errorCodes: error.response.data.errorCodes,
        errorMessage: error.response.data.message,
        traceId: error.response.data.traceId,
      },
    };

    fetch('/v1/trialsession/log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }).catch(() => {});
  };

  /**
     * Handle error
     * @param error
     */
  const onError = (error) => {
    if (error.response) {
      logMagiclineError(error);
      setErrorCode(error.response.data.errorCodes[0]);
    }
    setIsLoading(false);
  };

  /**
     * Submit form data
     * @param values
     */
  function submitForm(values) {
    const formData = {
      studioId: values.studioId,
      studioName: values.studioName,
      studioEmail: values.studioEmail,
      studioStreet: values.studioStreet,
      studioPostalCode: values.studioPostalCode,
      studioLocality: values.studioLocality,
      sitePackage,
      startDateTime: values.dueDate.split('+')[0],
      endDateTime: values.dueDate.split('+')[1],
      leadCustomer: {
        firstname: values.firstName,
        lastname: values.lastName,
        email: values.email,
        phone: values.phonePrefix + values.phone,
        gender: values.gender,
        dateOfBirth: dayjs(values.dateOfBirth).format('YYYY-MM-DD'),
      }, // SPECIAL CASE for Magicline
      // Set trainerRequired always on true and add note with String "mit Trainer" or "ohne Trainer"
      trainerRequired: true,
      note: values.formOfTraining === 'true' ? t('withTrainer') : t('withoutTrainer'),
      marketingTermsAccepted: values.marketingTerms,
      generalTermsAccepted: values.generalTerms,
    };
    submittedFormValues.current = formData;
    setIsLoading(true);
    mutate(JSON.stringify(formData), { onSuccess, onError });
  }

  /**
     * Handle submit
     * @param values
     * @param actions
     */
  function handleSubmit(values, actions) {
    if (isLastStep) {
      submitForm(values);
    } else {
      setLastValidStep(activeStep + 1);
      setActiveStep(activeStep + 1);
      actions.setTouched({});
    }
  }

  /**
     * Scroll to top event
     */
  const scrollToStepper = () => {
    const muiError = document.querySelector('.Mui-error');
    if (muiError) {
      smoothScrollIntoView(muiError, {
        behavior: 'smooth', scrollMode: 'if-needed', block: 'center', inline: 'center',
      });
    } else {
      const node = document.querySelector('.form-stepper');
      smoothScrollIntoView(node, {
        behavior: 'smooth', scrollMode: 'if-needed', block: 'start', inline: 'center',
      });
    }
  };

  /**
     * Handle back
     */
  function handleBack() {
    setActiveStep(activeStep - 1);
    setErrorCode(null);
    setNextButtonIsActive(true);
    scrollToStepper();
  }

  /**
     * click on the stepper button
     * @param stepIndex
     */
  const changeActiveStep = (stepIndex) => {
    if (stepIndex <= lastValidStep) {
      setErrorCode(null);
      setActiveStep(stepIndex);
    }
  };

  return {
    firstStepHeader,
    firstStepContent,
    secondStepHeader,
    secondStepContent,
    thirdStepHeader,
    thirdStepContent,
    fourthStepHeader,
    fourthStepContent,
    confirmationHeader,
    confirmationContent,
    stepperLabels,
    activeStep,
    lastValidStep,
    setLastValidStep,
    changeActiveStep,
    isLastStep,
    errorCode,
    isLoading,
    scrollToStepper,
    handleSubmit,
    handleBack,
  };
};
